import {
  Component,
  OnInit,
  AfterViewInit,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
  inject,
  Input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { GoogleMapsModule, GoogleMap, MapMarker } from '@angular/google-maps';
import { Subject, from, of } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  filter,
  switchMap,
  catchError,
} from 'rxjs/operators';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { TranslateModule } from '@ngx-translate/core';
import { PlacesService } from '../../services/places.service';
import { TaxisService } from '../../services/order.service';
import { environment } from '../../../environments/environment';

declare var google: any;
interface PlacePrediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  lat?: number;
  lng?: number;
}

interface Marker {
  position: google.maps.LatLngLiteral;
  label?: google.maps.MarkerLabel;
  title?: string;
  options?: google.maps.MarkerOptions;
  place_id?: string;
}

@Component({
  selector: 'app-agm-map',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GoogleMapsModule,
    MatAutocompleteModule,
    TranslateModule
  ],
  providers: [PlacesService],
  templateUrl: './agm-map-component.html',
  styleUrls: ['./agm-map-component.scss'],
})
export class  AgmMapComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild('destinationInput') destinationInput!: ElementRef;
  @ViewChild('startInput') startInput!: ElementRef;
  @ViewChild('endInput') endInput!: ElementRef;

  private _unsubscribeAll: Subject<void> = new Subject<void>();
  predictions: PlacePrediction[] = [];
  startPredictions: PlacePrediction[] = [];
  endPredictions: PlacePrediction[] = [];
  isLoading = false;

  // Map properties - Centered on Marrakech
  center: google.maps.LatLngLiteral = {
    lat: 31.6295,
    lng: -7.9811,
  };
  zoom = 10; 
  options: google.maps.MapOptions = {
    mapTypeControl: false,
    zoomControl: false,
    scrollwheel: true,
    zoom: 10,
    disableDefaultUI: true,
    disableDoubleClickZoom: true,
    mapId:"cd2727d0d95a2774f951ece8"

  };

  markerSelection: boolean = false;
  googleMode: boolean = false;
  displayedKeyboard: boolean = false;
  selectedMarkerAddress: string = '';

  @Input()
  startPlaceId: string = '';
  @Input()
  endPlaceId: string = '';

  // Form controls
  destinationControl = new FormControl('');
  startControl = new FormControl('');
  endControl = new FormControl('');
  @Input()
  selectedSecteurFormOrigin!: FormGroup;

  //! DEPARTURE
  @Input()
  departureControl!: FormControl;

  //! DESTINATION
  @Input()
  arriveeControl!: FormControl;

  selectedSecteurForm = new FormGroup({
    arriveeSecteur: new FormControl(''),
    destination: new FormControl(''),
    arriveeLat: new FormControl(0),
    arriveeLng: new FormControl(0),
    lat: new FormControl(0),
    lng: new FormControl(0),
    arrivee_place_id: new FormControl(''),
    place_id: new FormControl(''),
    start: new FormControl(''),
    end: new FormControl(''),
  });

  // Markers
  departMarker: Marker | null = null;
  selectedMarker: Marker | null = null;

  // Directions service and renderer
  private directionsService = new google.maps.DirectionsService();
  private directionsRenderer: google.maps.DirectionsRenderer | null = null;

  @Output() directionDetails = new EventEmitter<any>();

  private _placesService = inject(PlacesService);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  // Input positions
  startInputPosition = { x: 0, y: 0 };
  endInputPosition = { x: 0, y: 0 };

  // Selection mode
  isSelectingStart: boolean = false;
  isSelectingEnd: boolean = false;

  // Advanced markers toggle
  useAdvancedMarkers: boolean = true;

  constructor(private _orderService: TaxisService) {}

  departPriceTag: any;
  destinationPriceTag: any;
  async ngOnInit() {
   

    // Create advanced marker after map is loaded
    setTimeout(() => {
      this.addAdvancedMarker('departure', this.center, 'Test Location');
    }, 2000);

    // Initialize start location autocomplete
    this.startControl.valueChanges
      .pipe(
        takeUntil(this._unsubscribeAll),
        debounceTime(500),
        filter(
          (query): query is string =>
            !!query && typeof query === 'string' && query.length >= 3
        ),
        switchMap((query: string) =>
          from(this._placesService.getPlacePredictions(query))
        ),
        catchError((error) => {
          console.error('Error fetching start predictions:', error);
          return of([]);
        })
      )
      .subscribe((predictions: PlacePrediction[]) => {
        console.log('Start Predictions:', predictions);
        this.startPredictions = predictions;
        this._changeDetectorRef.markForCheck();
      });

    // Initialize end location autocomplete
    this.endControl.valueChanges
      .pipe(
        takeUntil(this._unsubscribeAll),
        debounceTime(300),
        filter(
          (query): query is string =>
            !!query && typeof query === 'string' && query.length >= 3
        ),
        switchMap((query: string) =>
          from(this._placesService.getPlacePredictions(query))
        )
      )
      .subscribe((predictions: PlacePrediction[]) => {
        console.log('End Predictions:', predictions);
        this.endPredictions = predictions;
        this._changeDetectorRef.markForCheck();
      });

    // Initialize map with locations and setup form listeners
    await this.initializeLocationsFromForm();

    //! DEPARTURE
    this.selectedSecteurForm
      .get('place_id')
      ?.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300))
      .subscribe(async (placeId: any) => {
        console.log('Departure Place ID Changed:', placeId);
        if (placeId) {
          let form = this.selectedSecteurForm.getRawValue();

          //? Let's make the data readied for transfor to the first form
          let data = {
            place_id: placeId,
            lat: form?.lat,
            lng: form?.lng,
            address: form?.start ?? '',
          };
          console.log('Departure Data:', data);
          // alert('Departure Place ID Changed: ' + placeId);
          this.departureControl.setValue(data);
          this.startPlaceId = placeId;
          this._changeDetectorRef.markForCheck();
        }
      });

    //! ARRIVEE
    this.selectedSecteurForm
      .get('arrivee_place_id')
      ?.valueChanges.pipe(takeUntil(this._unsubscribeAll), debounceTime(300))
      .subscribe(async (placeId: any) => {
        console.log('Arrivee Place ID Changed:', placeId);
        if (placeId) {
          let form = this.selectedSecteurForm.getRawValue();
          //? Let's make the data readied for transfor to the first form
          let data = {
            place_id: placeId,
            lat: form?.arriveeLat,
            lng: form?.arriveeLng,
            address: form?.end ?? '',
          };

          this.arriveeControl.setValue(data);
          this.endPlaceId = placeId;
          this._changeDetectorRef.markForCheck();
        }
      });

    //! DEPARTURE CHANGED
    this._orderService.departureData$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (data: any) => {
        console.log('Departure Data Changed:', data);
        if (data) {
          this.selectedSecteurForm.patchValue(
            {
              place_id: data.initial_place_id,
              lat: data.initialLat,
              lng: data.initialLng,
              start: data.initialAddress,
            },
            { emitEvent: false }
          );
          this.startControl.setValue(data.initialAddress, { emitEvent: false });
          this.selectedSecteurForm.updateValueAndValidity();
          this._changeDetectorRef.markForCheck();
        }
      });
    //! DESTINATION CHANGED
    this._orderService.destinationData$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(async (data: any) => {
        console.log('Destination Data Changed:', data);
        if (data) {
          this.selectedSecteurForm.patchValue(
            {
              arrivee_place_id: data.destination_place_id,
              arriveeLat: data.destinationLat,
              arriveeLng: data.destinationLng,
              destination: data.destinationAddress,
            },
            { emitEvent: false }
          );
          this.endControl.setValue(data.destinationAddress, {
            emitEvent: false,
          });
          this.selectedSecteurForm.updateValueAndValidity();
          this._changeDetectorRef.markForCheck();
        }
      });
  }
  // Initialize locations from form values
  async initializeLocationsFromForm() {
  //  alert('Initializing locations from form values...');
    try {
      // Initialize start location if place ID exists
      const startPlaceId =
        this.selectedSecteurFormOrigin.get('initial_place_id')?.value;
        console.log('Start Place ID:', startPlaceId);
        console.log('Selected Secteur Form Origin:', this.selectedSecteurFormOrigin.value);
      if (startPlaceId) {
        this.startPlaceId = startPlaceId;
        console.log('Start Place ID:', this.startPlaceId);
        await this.initStartByPlaceId(this.startPlaceId);
      }

      // Initialize end location if place ID exists
      const endPlaceId = this.selectedSecteurFormOrigin.get(
        'destination_place_id'
      )?.value;
      if (endPlaceId) {
      
        this.endPlaceId = endPlaceId;
        console.log('End Place ID:', this.endPlaceId);
        await this.initEndByPlaceId(this.endPlaceId);
      }
    } catch (error) {
      console.error('Error initializing locations:', error);
    }
  }



  ngAfterViewInit(): void {
    // Initialize DirectionsRenderer with retry mechanism
    const initRenderer = () => {
      if (this.map?.googleMap) {
        this.directionsRenderer = new google.maps.DirectionsRenderer({
          polylineOptions: {
            strokeColor: '#6565fa', // Ensure blue polyline
            strokeOpacity: 0.8,
            strokeWeight: 6,
          },
          suppressMarkers: true, // Suppress default markers
        });
        if (this.directionsRenderer)
          this.directionsRenderer.setMap(this.map.googleMap);
        console.log('DirectionsRenderer initialized with blue polyline and suppressed markers');
      } else {
        console.warn(
          'Map not yet loaded, retrying DirectionsRenderer initialization...'
        );
        // setTimeout(initRenderer, 500); // Retry after 500ms
      }
    };
    initRenderer();

    // Add listener for map movement
    if (this.map?.googleMap) {
      this.map.googleMap.addListener('bounds_changed', () => {
        this.updateInputPositions();
      });

      this.map.googleMap.addListener('zoom_changed', () => {
        this.updateInputPositions();
      });
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
    if (this.directionsRenderer) {
      this.directionsRenderer.setMap(null);
    }
    this.clearAdvancedMarkers();
  }

  // Handle search button click
  async onSearch(): Promise<void> {
    if (!this.startControl.value || !this.endControl.value) {
      alert('Veuillez sélectionner un point de départ et une destination.');
      return;
    }
    if (!this.directionsRenderer) {
      alert(
        'Erreur d’initialisation de la carte. Veuillez rafraîchir la page.'
      );
      return;
    }

    console.log(
      'Recherche d’itinéraire de:',
      this.startControl.value,
      'vers:',
      this.endControl.value
    );
    await this.calculateAndDisplayRoute();
  }

  /**
   * Calculate and display route
   * @param target Define which target to calculate route for (1: start, 2: end)
   */
  private async calculateAndDisplayRoute(target: 1 | 2 = 1, sendEvent = true): Promise<void> {
    try {
      const response = await this.directionsService.route({
        origin: { placeId: this.startPlaceId }, // ID du lieu de départ
        destination: { placeId: this.endPlaceId }, // ID du lieu d'arrivée
        travelMode: google.maps.TravelMode.DRIVING,
      });

      console.log('Réponse Directions:', response);
      if (response.routes.length === 0) {
        throw new Error('Aucun itinéraire trouvé.');
      }

      // Render the route
      this.directionsRenderer!.setDirections(response);
      console.log('Itinéraire affiché sur la carte');

      // Update markers for start and end points
      const route: any = response.routes[0].legs[0];
      console.log('Route:', route);
      this.departMarker = {
        position: {
          lat: route.start_location.lat(),
          lng: route.start_location.lng(),
        },
        // label: {
        //   color: '#00FF00',
        //   fontSize: '20px',
        //   fontWeight: 'bold',
        //   text: route.start_address,
        // },
        title: this.startControl.value  || '',
      };
      this.selectedMarker = {
        position: {
          lat: route.end_location.lat(),
          lng: route.end_location.lng(),
        },
        // label: {
        //   color: '#FF0000',
        //   fontSize: '20px',
        //   fontWeight: 'bold',
        //   text: route.end_address,
        // },
        title: this.endControl.value || ''
      };

      // Create advanced markers if enabled
      if (this.useAdvancedMarkers) {
        this.departPriceTag = this.createPriceTag('departure', this.startControl.value || route.start_address);
        this.destinationPriceTag = this.createPriceTag('destination', this.endControl.value || route.end_address);
        this._changeDetectorRef.markForCheck();
      }

      // Update form with start and end details
      this.selectedSecteurForm.patchValue({
        start: route.start_address,
        end: route.end_address,
        lat: route.start_location.lat(),
        lng: route.start_location.lng(),
        arriveeLat: route.end_location.lat(),
        arriveeLng: route.end_location.lng(),
      });

      // Emit route details
      this.directionDetails.emit({
        start: {
          address: route.start_address,
          lat: route.start_location.lat(),
          lng: route.start_location.lng(),
        },
        end: {
          address: route.end_address,
          lat: route.end_location.lat(),
          lng: route.end_location.lng(),
        },
        distance: route.distance.text,
        duration: route.duration.text,
      });

      if (target === 1) {
        let data = {
          place_id: this.startPlaceId,
          lat: route.start_location.lat(),
          lng: route.start_location.lng(),
          address: this.startControl.value || route.start_address,
        };
        console.log('Departure Data:', data);
        // alert()
        this.departureControl.setValue(data,{ emitEvent: sendEvent });
        this.startPredictions = [];
      } else if (target === 2) {
        let data = {
          place_id: this.endPlaceId,
          lat: route.end_location.lat(),
          lng: route.end_location.lng(),
          address: this.endControl.value || route.end_address,
        };

        this.arriveeControl.setValue(data,{ emitEvent: sendEvent });
        this.endPredictions = [];
      }

      // Fit map to route bounds
      if (this.map.googleMap) {
        const bounds = new google.maps.LatLngBounds();

        
        bounds.extend(route.start_location);
        bounds.extend(route.end_location);
        console.log('Bounds:', bounds.toString());
        setTimeout(() => {
            this.map.googleMap?.fitBounds(bounds, {
              top: 300,
              bottom: 100,
              left: 50,
              right: 50,
            });
          }, 500);

        console.log('Carte centrée avec padding personnalisé (Point B en bas)');
      }
      else {
        console.error('Carte non disponible pour ajuster les limites');
        // Fallback: Center on start point
        this.center = {
          lat: route.start_location.lat(),
          lng: route.start_location.lng(),
        };
        this.zoom = 10;
      }

      this._changeDetectorRef.markForCheck();
    } catch (error) {
      console.error('Échec de la requête Directions:', error);
      alert(
        'Impossible de calculer l’itinéraire. Vérifiez vos sélections ou réessayez plus tard.'
      );
    }
  }

  // Mode selection buttons
  startSelection(): void {
    this.isSelectingStart = true;
    this.isSelectingEnd = false;
  }

  endSelection(): void {
    this.isSelectingStart = false;
    this.isSelectingEnd = true;
  }

  cancelSelection(): void {
    this.isSelectingStart = false;
    this.isSelectingEnd = false;
  }
 haversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (x: number) => x * Math.PI / 180;
  const R = 6371e3; // rayon de la Terre en mètres

  const φ1 = toRad(lat1);
  const φ2 = toRad(lat2);
  const Δφ = toRad(lat2 - lat1);
  const Δλ = toRad(lng2 - lng1);

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // en mètres
}

async searchNearbyPlaces(lat: number, lng: number): Promise<any[]> {
  console.log('Searching nearby places for:', lat, lng);
  const apiKey = environment.apiKeyGoogleMap;
  const response = await fetch(
    `https://places.googleapis.com/v1/places:searchNearby`, 
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.location,places.types,places.id'
      },
      body: JSON.stringify({
        maxResultCount: 10,
        locationRestriction: {
          circle: {
            center: { latitude: lat, longitude: lng },
            radius: 50 // ou plus large que 10 mètres
          }
        }
      })
    }
  );

  if (!response.ok) {
    throw new Error('Erreur lors de la recherche de lieux');
  }

  const data = await response.json();

  const sortedPlaces = (data.places || []).map((place: any) => {
    const placeLat = place.location.latitude;
    const placeLng = place.location.longitude;
    const distance = this.haversineDistance(lat, lng, placeLat, placeLng);
    return { ...place, distance };
  }).sort((a:any, b:any) => a.distance - b.distance);

  console.log('Sorted Places:', sortedPlaces);
  return sortedPlaces;
}
 splitAndRemoveLastTwo(address: string): string {
  const parts = address.split(',').map(p => p.trim());

  if (parts.length <= 2) return parts[0]; // Rien à garder

  return parts.slice(0, -2).join(', ');
}



  async onMapInitialized(event: google.maps.MapMouseEvent): Promise<void> {
    if (!event.latLng || (!this.isSelectingStart && !this.isSelectingEnd))
      return;

    console.log('CARTE PRÊTE !!');
    console.log('Événement !!', event);

    const clickedData = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

  const lat = event.latLng.lat();
  const lng = event.latLng.lng();


    try {
    // const geocoder = new google.maps.Geocoder();
    // let geocodeResponse = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
    //   geocoder.geocode({ location: clickedData }, (results:any, status:any) => {
    //     if (status === google.maps.GeocoderStatus.OK && results) {
    //       resolve(results);
    //     } else {
    //       reject(status);
    //     }
    //   });
    // });


    // Appliquer le filtre
    // console.log('Géocodage before filtering', geocodeResponse);
    // const filteredResults = geocodeResponse.filter(isValidAddress);
    // geocodeResponse=filteredResults
    const places = await this.searchNearbyPlaces(lat, lng);

    if (places.length === 0) {
      console.warn('Aucun lieu trouvé à proximité');

    // 👇 Centre la carte sur la position et fait un zoom élevé
    this.map.panTo({ lat, lng });
    this.zoom=16;
      return;
    }

    const bestMatch = places[0]; // ou affiche-les tous
    console.log('Lieu trouvé :', bestMatch.displayName.text);
    console.log('Adresse :', bestMatch.formattedAddress);
    console.log('Types :', bestMatch.types);

      console.log('Géocodage réussi:', bestMatch);
      console.log('Is Selecting Start:', this.isSelectingStart);
      console.log('Is Selecting End:', this.isSelectingEnd);
      if (bestMatch.id) {
        const place_id = bestMatch.id;

        if (this.isSelectingStart) {
          this.startPlaceId = place_id;
          this.departMarker = {
            position: clickedData,
            // label: {
            //   color: '#00FF00',
            //   fontSize: '20px',
            //   fontWeight: 'bold',
            //   text: geocodeResponse[0].formatted_address,
            // },
            // title: 'Point de départ',
            place_id: place_id,
          };
          this.startControl.setValue(bestMatch.displayName.text, {
            emitEvent: false,
          });

          // Create departure price tag
          this.departPriceTag = this.createPriceTag('departure', bestMatch.displayName.text);

          // Create advanced marker if enabled
          // if (this.useAdvancedMarkers) {
          //   this.addAdvancedMarker('departure', clickedData, geocodeResponse[0].formatted_address);
          // }
        } else if (this.isSelectingEnd) {
          this.endPlaceId = place_id;
          this.selectedMarker = {
            position: clickedData,
            // label: {
            //   color: '#FF0000',
            //   fontSize: '20px',
            //   fontWeight: 'bold',
            //   text: geocodeResponse[0].formatted_address,
            // },
            // title: 'Destination',
            place_id: place_id,
          };
          this.endControl.setValue(this.splitAndRemoveLastTwo(bestMatch.displayName.text), {
            emitEvent: false,
          });

          // Create destination price tag
          this.destinationPriceTag = this.createPriceTag('destination', this.splitAndRemoveLastTwo(bestMatch.displayName.text));

          // Create advanced marker if enabled
          // if (this.useAdvancedMarkers) {
          //   this.addAdvancedMarker('destination', clickedData, geocodeResponse[0].formatted_address);
          // }
        }

        // Update form and calculate route if both points are set
        if (this.startPlaceId && this.endPlaceId) {
          await this.calculateAndDisplayRoute(this.isSelectingEnd ? 2 : 1, true);
        }

        this.isSelectingStart = false;

        this.isSelectingEnd = false;
        this.updateInputPositions();

        this._changeDetectorRef.markForCheck();
      }
    } catch (error) {
      console.error('Erreur de géocodage:', error);
    }
  }

  // Handler for input changes
  onStartInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      this.startPredictions = [];
    }
  }

  onEndInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.value) {
      this.endPredictions = [];
    }
  }

  // Advanced markers
  private departAdvancedMarker: google.maps.marker.AdvancedMarkerElement | null = null;
  private destinationAdvancedMarker: google.maps.marker.AdvancedMarkerElement | null = null;

  addAdvancedMarker(type: 'departure' | 'destination', position: google.maps.LatLngLiteral, address: string) {
    if (!this.map?.googleMap) {
      console.log('Map not ready yet');
      return;
    }

    // Check if AdvancedMarkerElement is available
    if (!google.maps.marker?.AdvancedMarkerElement) {
      console.warn('AdvancedMarkerElement not available, using standard markers');
      this.useAdvancedMarkers = false;
      return;
    }

    // Remove existing marker of the same type
    if (type === 'departure' && this.departAdvancedMarker) {
      this.departAdvancedMarker.map = null;
    }
    if (type === 'destination' && this.destinationAdvancedMarker) {
      this.destinationAdvancedMarker.map = null;
    }

    // Create content using priceTag as base
    const isDestination = type === 'destination';
    
    // Customize the content
    // content.style.cssText = `
    //   background-color: white;
    //   border-radius: 8px;
    //   padding: 8px 12px;
    //   box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    //   border-left: 4px solid ${isDestination ? '#ef4444' : '#22c55e'};
    //   font-size: 12px;
    //   font-weight: 500;
    //   max-width: 200px;
    //   position: relative;
    // `;
    
    // content.innerHTML = `

    //   <div class="price-tag">
    //     ${address}
    //   </div>
    // `;

    // try {
    //   const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
    //     map: this.map.googleMap,
    //     position: position,
    //     content: content,
    //     title: isDestination ? 'Destination' : 'Départ',
    //   });

    //   // Store reference
    //   if (type === 'departure') {
    //     this.departAdvancedMarker = advancedMarker;
    //   } else {
    //     this.destinationAdvancedMarker = advancedMarker;
    //   }
      
    //   console.log('Advanced marker created successfully:', type);
    // } catch (error) {
    //   console.error('Error creating AdvancedMarkerElement:', error);
    //   this.useAdvancedMarkers = false;
    // }
  }

  // addAdvancedMarker(type: 'departure' | 'destination', position: google.maps.LatLngLiteral, address: string) {
  //   if (!this.map?.googleMap) return;

  //   // Check if AdvancedMarkerElement is available
  //   if (!google.maps.marker?.AdvancedMarkerElement) {
  //     console.warn('AdvancedMarkerElement not available, using standard markers');
  //     this.useAdvancedMarkers = false;
  //     return;
  //   }

  //   // Remove existing marker of the same type
  //   if (type === 'departure' && this.departAdvancedMarker) {
  //     this.departAdvancedMarker.map = null;
  //   }
  //   if (type === 'destination' && this.destinationAdvancedMarker) {
  //     this.destinationAdvancedMarker.map = null;
  //   }

  //   const content = document.createElement('div');
  //   const isDestination = type === 'destination';
    
  //   // content.innerHTML = `
  //   //   <div style="
  //   //     background-color: white; 
  //   //     border-radius: 8px; 
  //   //     padding: 8px 12px; 
  //   //     box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  //   //     border-left: 4px solid ${isDestination ? '#ef4444' : '#22c55e'};
  //   //     max-width: 200px;
  //   //     font-size: 12px;
  //   //     font-weight: 500;
  //   //   ">
  //   //     <div style="color: ${isDestination ? '#ef4444' : '#22c55e'}; font-weight: bold; margin-bottom: 4px;">
  //   //       ${isDestination ? '🎯 Destination' : '📍 Départ'}
  //   //     </div>
  //   //     <div style="color: #374151; font-size: 11px; line-height: 1.2;">
  //   //       ${address}
  //   //     </div>
  //   //   </div>
  //   // `;

  //   // try {
  //   //   const advancedMarker = new google.maps.marker.AdvancedMarkerElement({
  //   //     map: this.map.googleMap,
  //   //     position: position,
  //   //     content: content,
  //   //     // title: isDestination ? 'Destination' : 'Départ',
  //   //   });

  //   //   // Store reference
  //   //   if (type === 'departure') {
  //   //     this.departAdvancedMarker = advancedMarker;
  //   //   } else {
  //   //     this.destinationAdvancedMarker = advancedMarker;
  //   //   }
  //   // } catch (error) {
  //   //   console.error('Error creating AdvancedMarkerElement:', error);
  //   //   this.useAdvancedMarkers = false;
  //   // }
  // }

  private clearAdvancedMarkers() {
    if (this.departAdvancedMarker) {
      this.departAdvancedMarker.map = null;
      this.departAdvancedMarker = null;
    }
    if (this.destinationAdvancedMarker) {
      this.destinationAdvancedMarker.map = null;
      this.destinationAdvancedMarker = null;
    }
  }

  toggleAdvancedMarkers() {
    this.useAdvancedMarkers = !this.useAdvancedMarkers;
    
    if (this.useAdvancedMarkers) {
      // Create advanced markers if we have positions
      if (this.departMarker) {
        this.addAdvancedMarker('departure', this.departMarker.position, this.startControl.value || 'Départ');
      }
      if (this.selectedMarker) {
        this.addAdvancedMarker('destination', this.selectedMarker.position, this.endControl.value || 'Destination');
      }
    } else {
      // Clear advanced markers
      this.clearAdvancedMarkers();
    }
  }

  private updateInputPositions(): void {
    if (this.map?.googleMap) {
      // Update start input position if we have a departure marker
      if (this.departMarker?.position) {
        const projection = this.map.googleMap.getProjection();
        if (projection) {
          const point:any= projection.fromLatLngToPoint(
            new google.maps.LatLng(
              this.departMarker.position.lat,
              this.departMarker.position.lng
            )
          );
          const bounds = this.map.googleMap.getBounds();
          if (bounds) {
            const ne:any = projection.fromLatLngToPoint(bounds.getNorthEast());
            const sw:any = projection.fromLatLngToPoint(bounds.getSouthWest());
            const scale = Math.pow(2, this.map.googleMap.getZoom() || 0);

            this.startInputPosition = {
              x: (point.x - sw.x) * scale,
              y: (point.y - ne.y) * scale,
            };
          }
        }
      }

      // Update end input position if we have a destination marker
      if (this.selectedMarker?.position) {
        const projection = this.map.googleMap.getProjection();
        if (projection) {
          const point:any = projection.fromLatLngToPoint(
            new google.maps.LatLng(
              this.selectedMarker.position.lat,
              this.selectedMarker.position.lng
            )
          );
          const bounds = this.map.googleMap.getBounds();
          if (bounds) {
            const ne:any = projection.fromLatLngToPoint(bounds.getNorthEast());
            const sw:any = projection.fromLatLngToPoint(bounds.getSouthWest());
            const scale = Math.pow(2, this.map.googleMap.getZoom() || 0);

            this.endInputPosition = {
              x: (point.x - sw.x) * scale,
              y: (point.y - ne.y) * scale,
            };
          }
        }
      }
      this._changeDetectorRef.markForCheck();
    }
  }

  calculateAllTTC(): void {
    console.log('Calcul TTC...');
  }

  async initStartByPlaceId(placeId: string): Promise<void> {
    // alert('Initialisation du point de départ...');
    try {
      console.log('Initializing start by Place ID:', placeId);
      const placeDetails = await this._placesService.getPlaceById(placeId);
      const coordinates = await this._placesService.getCoordinatesFromPlaceId(
        placeId
      );
      const address = placeDetails.name || 'Location non trouvée';

      this.startPlaceId = placeId;

      // Update start marker
      this.departMarker = {
        position: { lat: coordinates.lat, lng: coordinates.lng },
        // label: {
        //   color: '#00FF00',
        //   fontSize: '20px',
        //   fontWeight: 'bold',
        //   text: address,
        // },
        // title: 'Point de départ',
        place_id: placeId,
      };

      // Create departure price tag
      this.departPriceTag = this.createPriceTag('departure', address);

      // Update form
      console.log('Setting start control value:', address);
      this.startControl.setValue(address, { emitEvent: false });
      this.selectedSecteurForm.patchValue({
        lat: coordinates.lat,
        lng: coordinates.lng,
        place_id: placeId,
        start: address,
      });

      // If we have both start and end points, calculate route
      if (this.endPlaceId) {
        await this.calculateAndDisplayRoute(1,false);
      }

      // Create advanced marker if enabled
      // if (this.useAdvancedMarkers) {
      //   this.addAdvancedMarker('departure', coordinates, address);
      // }
    } catch (error) {
      console.error(
        "Erreur lors de l'initialisation du point de départ:",
        error
      );
    }
  }

  async initEndByPlaceId(placeId: string): Promise<void> {
    try {
      // alert('Initialisation de la destination...');
      console.log('Initializing end by Place ID:', placeId);
      const placeDetails = await this._placesService.getPlaceById(placeId);
      const coordinates = await this._placesService.getCoordinatesFromPlaceId(
        placeId
      );
      console.log('Place Details:', placeDetails);
      const address = placeDetails.name || 'Location non trouvée';

      this.endPlaceId = placeId;

      // Update end marker
      this.selectedMarker = {
        position: { lat: coordinates.lat, lng: coordinates.lng },
        // label: {
        //   color: '#FF0000',
        //   fontSize: '20px',
        //   fontWeight: 'bold',
        //   text: address,
        // },
        // title: 'Destination',
        place_id: placeId,
      };

      // Create destination price tag
      this.destinationPriceTag = this.createPriceTag('destination', address);

      // Update form
      this.endControl.setValue(address, { emitEvent: false });
      this.selectedSecteurForm.patchValue({
        arriveeSecteur: address,
        destination: address,
        arriveeLat: coordinates.lat,
        arriveeLng: coordinates.lng,
        arrivee_place_id: placeId,
      });

      // If we have both start and end points, calculate route
      if (this.startPlaceId) {
        await this.calculateAndDisplayRoute(2,false);
      }

      // Create advanced marker if enabled
      // if (this.useAdvancedMarkers) {
      //   this.addAdvancedMarker('destination', coordinates, address);
      // }
    } catch (error) {
      console.error(
        "Erreur lors de l'initialisation de la destination:",
        error
      );
    }
  }

  async selectEndLocation(prediction: PlacePrediction): Promise<void> {
    try {
      console.log('Sélection de la destination:', prediction);
      // const placeDetails = await this._placesService.getPlaceById(
      //   prediction.place_id
      // );
      // const coordinates = await this._placesService.getCoordinatesFromPlaceId(
      //   prediction.place_id
      // );

      this.endPlaceId = prediction.place_id;

      // Update end marker
      this.selectedMarker = {
        position: { lat: prediction.lat ?? 0, lng: prediction.lng ?? 0 },
        // label: {
        //   color: '#FF0000',
        //   fontSize: '20px',
        //   fontWeight: 'bold',
        //   text: placeDetails.formatted_address || prediction.description,
        // },
        // title: 'Destination',
        place_id: prediction.place_id,
      };

      // Update form
      this.endControl.setValue(
        prediction.structured_formatting.main_text || prediction.description,
        { emitEvent: false }
      );
      this.selectedSecteurForm.patchValue({
        destination: prediction.structured_formatting.main_text || prediction.description,
        arriveeLat: prediction.lat,
        arriveeLng: prediction.lng,
        arrivee_place_id: prediction.place_id,
      });

      // Clear predictions
      this.endPredictions = [];

      // Create destination price tag
      this.destinationPriceTag = this.createPriceTag('destination', prediction.structured_formatting.main_text || prediction.description);

      // Create advanced marker if enabled
      // if (this.useAdvancedMarkers) {
      //   this.addAdvancedMarker('destination', coordinates, prediction.structured_formatting.main_text || prediction.description);
      // }

      // If we have both start and end points, calculate route
      if (this.startPlaceId) {
        await this.calculateAndDisplayRoute(1);
      }
    } catch (error) {
      console.error('Erreur lors de la sélection de la destination:', error);
    }
  }
  /**
   *
   * @param prediction
   * @param target 1 | 2 - 1 for start, 2 for end
   */
  async selectStartLocation(prediction: PlacePrediction): Promise<void> {
    try {
      // const placeDetails = await this._placesService.getPlaceById(
      //   prediction.place_id
      // );
      // console.log('Place Details:', placeDetails);
      // const coordinates = await this._placesService.getCoordinatesFromPlaceId(
      //   prediction.place_id
      // );
      // console.log('Coordinates:', coordinates);
      this.startPlaceId = prediction.place_id;

      // Update start marker
      this.departMarker = {
        position: { lat: prediction.lat ?? 0, lng: prediction.lng ?? 0 },
        // label: {
        //   color: '#00FF00',
        //   fontSize: '20px',
        //   fontWeight: 'bold',
        //   text: placeDetails.formatted_address || prediction.description,
        // },
        title: 'Point de départ',
        place_id: prediction.place_id,
      };

      // Update form
      this.startControl.setValue(
        prediction.structured_formatting.main_text || prediction.description,
        { emitEvent: false }
      );
      this.selectedSecteurForm.patchValue({

        destination: prediction.structured_formatting.main_text || prediction.description,
        lat: prediction.lat,
        lng: prediction.lng,
        place_id: prediction.place_id,
      });
      // Clear predictions
      this.startPredictions = [];

      // Create departure price tag
      this.departPriceTag = this.createPriceTag('departure', prediction.structured_formatting.main_text || prediction.description);

      // Create advanced marker if enabled
      // if (this.useAdvancedMarkers) {
      //   this.addAdvancedMarker('departure', coordinates, prediction.structured_formatting.main_text || prediction.description);
      // }

      // If we have both start and end points, calculate route
      if (this.endPlaceId) {
        await this.calculateAndDisplayRoute(2);
      }
    } catch (error) {
      console.error('Erreur lors de la sélection du point de départ:', error);
    }
  }

  displayFn(): (item: any) => string {
    return (item: any): string => {
      console.log('DISPLAY FN', item);

      if (item && item !== '') {
        return item && item.title
          ? item.title
          : item.description
          ? item.description
          : item.displayTitle
          ? item.displayTitle
          : '';
      } else {
        return '';
      }
    };
  }
  t(t: string) {
    return t;
  }

  // Create different price tags for different marker types
  createPriceTag(type: 'departure' | 'destination', address?: string): HTMLElement {
    const priceTag = document.createElement('div');
    

    

    
    priceTag.innerHTML = `
      <div class="price-tag" >
       ${address}
      </div>
    `;
    
    return priceTag;
  }
}
